import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore";
import { supabase } from "../services/supabase";
import { matchingService } from "../services/matchingService";
import { WEB_AUTH_UI_ENABLED } from "../config/publicConfig";

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { user, setSession, fetchProfile } = useAuthStore();
  const [checking, setChecking] = React.useState(true);

  useEffect(() => {
    let mounted = true;

    async function checkSession() {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (mounted) {
          setSession(session);
          if (session) await fetchProfile();
        }
      } catch (error) {
        console.error("Session check failed", error);
      } finally {
        if (mounted) setChecking(false);
      }
    }

    checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (mounted) {
        setSession(session);
        if (session) await fetchProfile();
        setChecking(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [setSession, fetchProfile]);

  useEffect(() => {
    if (!checking && !user) {
      navigate(WEB_AUTH_UI_ENABLED ? "/login" : "/");
    } else if (user) {
      const inviteSlotId = localStorage.getItem("invite_slot_id");
      const inviteParentId = localStorage.getItem("invite_parent_id");
      
      if (inviteSlotId && inviteParentId) {
        matchingService.claimHandover({ slotId: inviteSlotId, parentId: inviteParentId })
          .then(() => {
            console.log("Successfully claimed handover slot!");
          })
          .catch(err => {
            console.error("Failed to claim handover slot:", err);
          })
          .finally(() => {
            localStorage.removeItem("invite_slot_id");
            localStorage.removeItem("invite_parent_id");
          });
      }

      const pendingShareCode = localStorage.getItem("pending_share_code");
      if (pendingShareCode) {
        matchingService.redeemShareCode(pendingShareCode)
          .then((result) => {
            if (result.success) {
              console.log("Successfully redeemed share code!");
            } else {
              console.error("Failed to redeem share code:", result.message);
            }
          })
          .catch(err => {
            console.error("Failed to redeem share code:", err);
          })
          .finally(() => {
            localStorage.removeItem("pending_share_code");
          });
      }
    }
  }, [checking, user, navigate]);

  if (checking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!user) return null;

  return <>{children}</>;
}
